import { getCookie } from 'react-use-cookie';
import { Avgiver } from 'svv-hradm-dinside-brukerhandtering';
import { hentSprak, tilGyldigNetsSprak } from './sprak';
import { hentFellesLoggtjenesteUrl, MILJO_BASEPATH } from './urls';

const API_BASE_PATH = '/ws/no/vegvesen/kjoretoy/kjennemerke/api/personlig';

/** Henter og returnerer den aktive avgiver-IDen.
 *
 * En avgiver-id er typisk organisasjonsnummeret til den bedriften eller enheten
 * en bruker handler på vegne av.
 *
 * Denne avgiver-iden sendes med requests til serveren via `avgiverId` headeren.
 */
const getAvgiverId = async () => {
    try {
        const resultat = await Avgiver.hent();
        if (resultat?.type === 'enhet') {
            return resultat.id;
        }
    } catch {
        // bare ignorer feil
    }
};

/** Setter sammen et standard header-objekt for API-requests
 *
 * Denne metoden henter ut avgiver-id, xsrf-token og returnerer alle headerne
 * man vanligvis trenger for å gjøre et API-request.
 */
const getHeaders = async (apen: boolean) => {
    let avgiverId = apen ? false : await getAvgiverId();

    const headers = new Headers({
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
    });

    if (avgiverId) {
        headers.append('avgiverId', avgiverId);
    }

    return headers;
};

export const leggGotoReturParameter = (loginpageUrl: string): string => {
    const viderekoblingsUrl = new URL(loginpageUrl);
    const sporsmalstegnEllerAmpersand = viderekoblingsUrl.search.length === 0 ? "?" : "&";
    return `${viderekoblingsUrl.href}${sporsmalstegnEllerAmpersand}goto=${encodeURIComponent(window.location.href)}`;
};

export const hentData = async <ReturnType>(
    url: string,
    args: RequestInit = {},
    useBasepath: boolean = true,
    apen: boolean = false,
) => {
    let headers = await getHeaders(apen);
    if (args.headers) {
        (args.headers as Headers).forEach((value: string, key: string) =>
            headers.append(key, value),
        );
    }

    const options: RequestInit = {
        headers,
        credentials: 'include',
        method: 'GET',
        ...args,
    };

    const fullUrl = (useBasepath ? MILJO_BASEPATH : '') + url;

    const response = await fetch(fullUrl, options);
    if (response.status >= 400) {
        if (response.status === 401) {
            const loginpageUrl = Object.fromEntries(response.headers)[
                'x-loginpage'
            ];
            if (loginpageUrl) {
                window.location.href = leggGotoReturParameter(loginpageUrl);
            }
        }
        throw new Error(
            `${response.status} ${response.statusText}, url: ${fullUrl}`,
        );
    }
    const json = await response.json();
    return json as ReturnType;
};

const postData = <ReturnType>(
    url: string,
    body?: any,
    args: RequestInit = {},
    useBasepath: boolean = true,
    apen: boolean = false,
) => {
    return hentData<ReturnType>(
        url,
        {
            ...args,
            body: JSON.stringify(body),
            method: 'POST',
        },
        useBasepath,
        apen,
    );
};

/** Sender en feilmelding til felles loggtjeneste */
export const loggFeil = async (error: string) => {
    try {
        console.log(`Her skjedde det en feil: ${error}`);
        await postData(
            hentFellesLoggtjenesteUrl(),
            {
                url: window.location.href,
                kilde: 'perkjm',
                melding: error,
                severity: 'ERROR',
            },
            undefined,
            false,
            true,
        );
    } catch {
        /* Får vi ikke logget til server så 🤷‍♀️ */
    }
};

type Skiltkombinasjoner = ([string, string] | [string])[];

type SkiltformaterUndergrupper = {
    ordinart?: Skiltkombinasjoner;
    rallyTrialEnduro?: Skiltkombinasjoner;
    utenforOffentligVeg?: Skiltkombinasjoner;
    varebilKlasse2?: Skiltkombinasjoner;
} | null;

export type Skiltformater = {
    bilBussLastebilTrekkbil: SkiltformaterUndergrupper;
    mcMopedBeltemotorsykkelTraktor: SkiltformaterUndergrupper;
    tilhenger: SkiltformaterUndergrupper;
};

export type TegnkombinasjonStatus = {
    ok: boolean;
    melding: string;
    tegnkombinasjon: string;
    captchaVis?: boolean;
    captchaVerifisert?: boolean;
    gyldigeSkiltformat: Skiltformater;
};

export type KjoretoyResponse = KjoretoyMinimal[];

export type KjoretoyMinimal = {
    merkenavn: string;
    modell: string;
    kjennemerke: string;
    arsmodell: string;
};

export const hentTegnkombinasjonStatusInnlogget = async (
    tegnkombinasjon: string,
) =>
    await hentData<TegnkombinasjonStatus>(
        `${API_BASE_PATH}/soknad/kanOpprettesFor/${tegnkombinasjon}`,
    );

export const hentTegnkombinasjonStatus = async (
    tegnkombinasjon: string,
    captchaToken = '',
) =>
    await hentData<TegnkombinasjonStatus>(
        `${API_BASE_PATH}/oppslag/tegnkombinasjonstatus/${tegnkombinasjon}/${captchaToken}`,
        { credentials: 'omit' },
        true,
        true,
    );

export const hentGyldigeKjoretoy = async (tegnkombinasjon: string) =>
    await hentData<KjoretoyResponse>(
        `${API_BASE_PATH}/soknad/hentKjoretoy/${tegnkombinasjon}`,
    );

export type SoknadStatus = {
    ok: boolean;
    melding: string;
    dato: string;
    klokkeslett: string;
};

export const sendSoknad = async (tegnkombinasjon: string) =>
    await hentData<SoknadStatus>(
        `${API_BASE_PATH}/soknad/sendSoknad/${tegnkombinasjon}`,
        {
            method: 'POST',
        },
    );

export type PersonligKjennemerkeStatuser = PersonligKjennemerkeStatus[];

export type PersonligKjennemerkeStatus = {
    soknadId: number;
    rettighetId: number;
    tegnkombinasjon: string;
    sakStatus: SakStatus;
    avslagBegrunnelse: string;
    datoStatusEndret: string;
    aliasFor?: string;
};

export type SakStatus = AktivSakStatus | DineSkiltSakStatus | FullfortSakStatus;

export enum AktivSakStatus {
    MOTTATT = 'MOTTATT',
    UNDER_BEHANDLING = 'UNDER_BEHANDLING',
    INNVILGET = 'INNVILGET',
    BETALT = 'BETALT',
    SKILT_BESTILT = 'SKILT_BESTILT',
    SKILT_BESTILT_BRUKER = 'SKILT_BESTILT_BRUKER',
    SKILT_MOTTATT = 'SKILT_MOTTATT',
}

export enum DineSkiltSakStatus {
    KNYTTET = 'KNYTTET',
    SKILT_UTLEVERT = 'SKILT_UTLEVERT',
}

export enum FullfortSakStatus {
    AVSLAG = 'AVSLAG',
    AVSLAG_TRUKKET = 'AVSLAG_TRUKKET',
    BETALINGSFRIST_UTLOPT = 'BETALINGSFRIST_UTLOPT',
    BETALING_FORFALT = 'BETALING_FORFALT',
    TILBAKEKALT = 'TILBAKEKALT',
    OVERFORT = 'OVERFORT',
    SKILT_INNDRATT = 'SKILT_INNDRATT',
    SKILT_TILBAKELEVERT = 'SKILT_TILBAKELEVERT',
}

export const hentSoknadsStatuser = async () =>
    await hentData<PersonligKjennemerkeStatuser>(
        `${API_BASE_PATH}/soknad/statusoversikt`,
    );

type Betalingsinfo = {
    ordrenr: string;
    terminalUrlMobil: string;
    terminalUrlStandard: string;
};

export const hentBetalingsinfo = async (soknadId: number) =>
    await postData<Betalingsinfo>(
        `${API_BASE_PATH}/soknad/betal/start/${soknadId}`,
        null,
        { headers: new Headers({ LANG: tilGyldigNetsSprak(hentSprak()) }) },
    );

export type GyldigSkiltForKjoretoy = {
    ok: boolean;
    feilkode: string;
    kjennemerke: string;
    kjennemerkeType: string;
    kjennemerkestorrelseForan: string;
    kjennemerkestorrelseBak: string;
    kjoretoyMerke: string;
    kjoretoyModell: string;
    tegnkombinasjon: string;
    arsmodell: string;
};

export const hentGyldigSkiltForKjoretoy = async (
    rettighetPk: string,
    kjennemerke: string,
) =>
    await hentData<GyldigSkiltForKjoretoy>(
        `${API_BASE_PATH}/soknad/rettighet/${rettighetPk}/kanBestillesFor/${kjennemerke}`,
    );

type SkiltbestillingRequest = {
    rettighetId: string;
    trafikkstasjon: Trafikkstasjon;
    kjennemerkeStorrelse: string[];
    kjennemerkeType: string;
};
type SkiltbestillingResponse = {
    ok: boolean;
    melding: string;
    dato: string;
};
export const sendSkiltbestilling = async (
    skiltbestilling: SkiltbestillingRequest,
) =>
    await postData<SkiltbestillingResponse>(
        `${API_BASE_PATH}/soknad/rettighet/bestillKjennemerke`,
        skiltbestilling,
    );

export type Trafikkstasjoner = {
    trafikkstasjoner: Trafikkstasjon[];
};

export type Trafikkstasjon = {
    navn: string;
    besoksadresse: string;
    postadresse: string;
    fylke: string;
    svvcompanyid: string;
    trafikkstasjonsnr: number;
    tilbyr: TrafikkstasjonTjenester;
    apningstider: { [key: string]: string };

};

export type TrafikkstasjonTjenester = {
    skilttjenester: boolean;
};

export const hentTrafikkstasjoner = async () =>
    await hentData<Trafikkstasjoner>(
        '/dinside/trafikkstasjoner.json',
        {},
        false,
    );

export const trekkSoknad = async (soknadId: number) =>
    await postData<SoknadStatus>(`${API_BASE_PATH}/soknad/trekk/${soknadId}`);

export type KnytningRequest = {
    rettighetId: string;
    kjennemerke: string;
};

export type KnytningsjekkResponse = {
    ok: boolean;
    valideringsfeil: string[];
    kjoretoy: Kjoretoy;
};

export type Kjoretoy = {
    merkenavn: string;
    modell: string;
};

export const sendSjekkKnytning = async (knytning: KnytningRequest) =>
    await postData<KnytningsjekkResponse>(
        `${API_BASE_PATH}/soknad/rettighet/knytningsjekk`,
        knytning,
    );

export const sendKnytning = async (knytning: KnytningRequest) =>
    await postData<SoknadStatus>(
        `${API_BASE_PATH}/soknad/rettighet/knytt`,
        knytning,
    );

export const sendFjernKnytning = async (knytning: KnytningRequest) =>
    await postData<SoknadStatus>(
        `${API_BASE_PATH}/soknad/rettighet/fjernKnytning`,
        knytning,
    );

export const hentKjennemerkeBaseUrl = async (rettighetId: string) => {
    let avgiverId = await getAvgiverId();
    let baseurl = `${MILJO_BASEPATH}${API_BASE_PATH}/soknad/rettighet/hentKjennemerkebevis/${rettighetId}`;
    if (avgiverId) {
        return `${baseurl}?AVGIVERID=${avgiverId}`;
    }
    return baseurl;
};
